import { useMutation, useQuery } from '@tanstack/react-query'
import { httpClient, ROOT_URL } from '../App'

export type IBodyWithdrawal = {
  withdrawMethod: string
  currencyId: number
  feeAmount?: any
  networkId?: number
  withdrawAddress?: string
  amount: any
  note?: string
  smartUserCode?: string
  cryptoFee?: {
    id: number
    fee: number
    version: number
  }
}

export const useCryptoWithdraw = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: cryptoWithdraw,
  })
}

export function cryptoWithdraw(body: IBodyWithdrawal) {
  return httpClient.post(`${ROOT_URL}/api/v1/withdrawal-requests/create`, body)
}

export const useGetAvlBalanceCrypto = (
  assetConfigId: number,
  networkId: number,
  type: string
) => {
  return useQuery<{
    data: { data: { totalBalance: number; decimals: number } }
  }>(
    [
      `/api/v1/asset-config-groups/network/available-balance`,
      assetConfigId,
      networkId,
      type,
    ],
    async () => getAvlBalanceCrypto(assetConfigId, networkId),
    {
      enabled: !!assetConfigId && !!networkId && type === 'CRYPTO_NETWORK',
    }
  )
}

export function getAvlBalanceCrypto(assetConfigId: number, networkId: number) {
  return httpClient.get(
    `${ROOT_URL}/api/v1/asset-config-groups/${assetConfigId}/network/${networkId}/available-balance`
  )
}

export const useGetAvlBalanceInternal = (assetConfigId: number | undefined) => {
  return useQuery<{
    data: { data: { totalBalance: number; decimals: number } }
  }>(
    [`/api/v1/asset-config-groups/available-balance`, assetConfigId],
    async () => getAvlBalanceInternal(assetConfigId),
    {
      enabled: !!assetConfigId,
    }
  )
}

export function getAvlBalanceInternal(assetConfigId: number | undefined) {
  return httpClient.get(
    `${ROOT_URL}/api/v1/asset-config-groups/${assetConfigId}/available-balance`
  )
}

export const useGetMinimumCrypto = (
  assetConfigId: number,
  networkId: number,
  type: string
) => {
  return useQuery<{ data: { data: string } }>(
    [
      `/api/v1/asset-config-groups/network/minimum-withdraw`,
      assetConfigId,
      networkId,
      type,
    ],
    async () => getMinimumCrypto(assetConfigId, networkId),
    {
      enabled: !!assetConfigId && !!networkId && type === 'CRYPTO_NETWORK',
    }
  )
}

export function getMinimumCrypto(assetConfigId: number, networkId: number) {
  return httpClient.get(
    `${ROOT_URL}/api/v1/asset-config-groups/${assetConfigId}/network/${networkId}/minimum-withdraw`
  )
}

export const useGetMinimumInternal = (currencyId: number, type: string) => {
  return useQuery<{ data: { data: string } }>(
    [`/api/v1/asset-config-groups/minimum-withdraw`, currencyId, type],
    async () => getMinimumInternal(currencyId),
    {
      enabled: !!currencyId && type === 'SMART_USER',
    }
  )
}

export function getMinimumInternal(assetConfigId: number) {
  return httpClient.get(
    `${ROOT_URL}/api/v1/asset-config-groups/${assetConfigId}/minimum-withdraw`
  )
}

export const useGetFeeAmount = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: getFeeAmount,
  })
}

export function getFeeAmount(body: { currencyId: number; networkId?: number }) {
  return httpClient.post(
    `${ROOT_URL}/api/v1/withdrawal-requests/estimate-fee`,
    body
  )
}

export const useCancelWithdraw = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: cancelWithdraw,
  })
}

export function cancelWithdraw(id: string) {
  return httpClient.put(
    `${ROOT_URL}/api/v1/withdrawal-requests/cancel/wallet-order/${id}`
  )
}

export const useValidateAddress = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: validateAddress,
  })
}

export function validateAddress(body: {
  address?: string
  smartUID?: string
  amount: number
  receiveAmount: number
  fee: number
  currencyId: number
}) {
  return httpClient.post(
    `${ROOT_URL}/api/v1/withdrawal-requests/verify-recipient`,
    body
  )
}

export const useGetOneOrder = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: getOneOrder,
  })
}

export const getOneOrder = (orderId: number) => {
  return httpClient.get(`${ROOT_URL}/api/v1/orders/get-one/${orderId}`)
}

export const useExchangeData = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: exchangeData,
  })
}

export function exchangeData(body: {
  paymentAmount?: number
  receiveAmount?: number
  receiveCurrencyId?: string
  paymentCurrencyId?: string
  exchangeRate?: number
  direction: 'PAY_TO_RCV' | 'RCV_TO_PAY' | string
  usdToFeeCurrencyRate?: number
  feeConfigVersion?: string
}) {
  return httpClient.post(
    `${ROOT_URL}/api/v1/exchange_money_requests/create`,
    body
  )
}

export const useCancelExchange = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: cancelExchange,
  })
}

export function cancelExchange({
  orderId,
  status,
}: {
  orderId: string
  status: string
}) {
  return httpClient.patch(
    `${ROOT_URL}/api/v1/exchange_money_requests/user/update/${orderId}`,
    {
      orderId,
      status,
    }
  )
}
